import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import {
  MapContainer,
  TileLayer,
  Marker,
  ZoomControl,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./css/bleep-form.css";

import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const orangePin = L.divIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 42" width="28" height="42">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 28 14 28S28 24.5 28 14C28 6.268 21.732 0 14 0z" fill="#e66b2c"/>
    <circle cx="14" cy="14" r="6" fill="white"/>
  </svg>`,
  iconSize: [28, 42],
  iconAnchor: [14, 42],
  popupAnchor: [0, -42],
});

const DEFAULT_CENTER = [1.3733, 32.2903]; // Uganda
const DEFAULT_ZOOM = 6;

function ClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

function FlyToPin({ pin }) {
  const map = useMap();
  useEffect(() => {
    if (pin)
      map.flyTo([pin.lat, pin.lng], Math.max(map.getZoom(), 14), {
        animate: true,
        duration: 0.6,
      });
  }, [pin?.lat, pin?.lng]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function FlyToTarget({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target)
      map.flyTo([target.lat, target.lng], 15, { animate: true, duration: 0.8 });
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function InvalidateOnMount() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 350);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

const LocationPicker = ({
  show,
  initialPin,
  onCancel,
  onProceed,
  defaultCity,
}) => {
  const [pin, setPin] = useState(null);
  const [address, setAddress] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const searchTimer = useRef(null);
  const citySearchTimer = useRef(null);

  useEffect(() => {
    if (show) {
      setPin(initialPin || null);
      setAddress(initialPin?.display || "");
      setGeoError("");
      setSearchQuery("");
      setSearchResults([]);
      setFlyTarget(null);

      // Auto-search and pin the selected city if no pin exists yet
      if (!initialPin && defaultCity) {
        clearTimeout(citySearchTimer.current);
        citySearchTimer.current = setTimeout(async () => {
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(defaultCity)}&format=json&countrycodes=ug,ke&limit=1`,
              { headers: { "Accept-Language": "en" } },
            );
            const data = await res.json();
            if (data && data[0]) {
              const latlng = {
                lat: parseFloat(data[0].lat),
                lng: parseFloat(data[0].lon),
              };
              setFlyTarget({ ...latlng, _t: Date.now() });
              setPin(latlng);
              setAddress(data[0].display_name || defaultCity);
              setSearchQuery(defaultCity);
            }
          } catch {
            // silently ignore — user can search manually
          }
        }, 500); // wait for map to initialise (InvalidateOnMount fires at 350ms)
      }
    }
    return () => clearTimeout(citySearchTimer.current);
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const reverseGeocode = useCallback(async (lat, lng) => {
    setGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
        { headers: { "Accept-Language": "en" } },
      );
      const data = await res.json();
      setAddress(data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    } catch {
      setAddress(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    } finally {
      setGeocoding(false);
    }
  }, []);

  const placePin = useCallback(
    (latlng) => {
      setPin(latlng);
      setSearchResults([]);
      reverseGeocode(latlng.lat, latlng.lng);
    },
    [reverseGeocode],
  );

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setSearchResults([]);
    clearTimeout(searchTimer.current);
    if (q.trim().length < 3) return;
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&countrycodes=ug,ke&limit=6`,
          { headers: { "Accept-Language": "en" } },
        );
        const data = await res.json();
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 420);
  };

  const handleSelectResult = (result) => {
    const latlng = { lat: parseFloat(result.lat), lng: parseFloat(result.lon) };
    setFlyTarget({ ...latlng, _t: Date.now() });
    placePin(latlng);
    setSearchQuery(result.display_name.split(",")[0]);
    setSearchResults([]);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }
    setLocating(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        placePin({ lat: coords.latitude, lng: coords.longitude });
        setLocating(false);
      },
      () => {
        setGeoError(
          "Could not get your location. Please allow location access.",
        );
        setLocating(false);
      },
      { timeout: 10000 },
    );
  };

  const handleProceed = () => {
    if (!pin || geocoding) return;
    onProceed({ lat: pin.lat, lng: pin.lng, display: address });
  };

  const initialCenter = initialPin
    ? [initialPin.lat, initialPin.lng]
    : DEFAULT_CENTER;
  const initialZoom = initialPin ? 14 : DEFAULT_ZOOM;

  return (
    <Modal
      show={show}
      onHide={onCancel}
      fullscreen="sm-down"
      centered
      dialogClassName="lp-dialog"
      contentClassName="bleep-light lp-modal"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title as="div" className="lp-title">
          <span className="lp-title__icon">
            <i className="fas fa-map-marker-alt" aria-hidden="true" />
          </span>
          <span className="lp-title__text">
            <span className="lp-title__heading">Pin the location</span>
            <span className="lp-title__sub">
              Search for it, tap the map, or use where you are now.
            </span>
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="lp-body">
        <div className="lp-stage">
          {/* Map */}
          {show && (
            <MapContainer
              key={show ? "lp-map-open" : "lp-map-closed"}
              center={initialCenter}
              zoom={initialZoom}
              className="lp-map"
              zoomControl={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ZoomControl position="bottomleft" />
              <ClickHandler onMapClick={placePin} />
              <FlyToPin pin={pin} />
              <FlyToTarget target={flyTarget} />
              <InvalidateOnMount />
              {pin && <Marker position={[pin.lat, pin.lng]} icon={orangePin} />}
            </MapContainer>
          )}

          {/* Search overlay */}
          <div className="lp-search">
            <div className="lp-search__box">
              <span className="lp-search__icon">
                <i
                  className={`fas ${
                    searchLoading ? "fa-spinner fa-spin" : "fa-search"
                  }`}
                  aria-hidden="true"
                />
              </span>
              <input
                type="text"
                className="lp-search__input"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search for a landmark or area"
                aria-label="Search for a landmark or area"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="lp-search__clear"
                  onClick={() => {
                    setSearchQuery("");
                    setSearchResults([]);
                  }}
                  aria-label="Clear search"
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              )}
            </div>

            {searchResults.length > 0 && (
              <ul className="lp-results">
                {searchResults.map((r, i) => {
                  const parts = r.display_name.split(",");
                  const primary = parts[0].trim();
                  const secondary = parts.slice(1, 3).join(",").trim();
                  return (
                    <li key={`${r.lat}-${r.lon}-${i}`}>
                      {/* A button, not an li with onClick: these were
                          unreachable by keyboard, and the hover was two inline
                          JS handlers writing style on the element. */}
                      <button
                        type="button"
                        className="lp-result"
                        onClick={() => handleSelectResult(r)}
                      >
                        <i
                          className="fas fa-map-marker-alt lp-result__icon"
                          aria-hidden="true"
                        />
                        <span className="lp-result__text">
                          <span className="lp-result__primary">{primary}</span>
                          {secondary && (
                            <span className="lp-result__secondary">
                              {secondary}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Directly under the search box, as the second way to answer the
                same question. The results list and this button take the same
                slot and are mutually exclusive, which is what the existing
                length check was already expressing. */}
            {searchResults.length === 0 && (
              <button
                type="button"
                className="lp-locate"
                onClick={handleUseMyLocation}
                disabled={locating}
              >
                <i
                  className={`fas ${
                    locating ? "fa-spinner fa-spin" : "fa-crosshairs"
                  }`}
                  aria-hidden="true"
                />
                {locating ? "Locating…" : "Use my current location"}
              </button>
            )}
          </div>

          {/* Bottom centre: remove the pin once there is one, otherwise say how
              to place one. */}
          {pin ? (
            <button
              type="button"
              className="lp-removepin"
              onClick={() => {
                setPin(null);
                setAddress("");
              }}
            >
              <i className="fas fa-trash-alt" aria-hidden="true" />
              Remove pin
            </button>
          ) : (
            searchResults.length === 0 && (
              <div className="lp-hint">
                <i className="fas fa-hand-pointer" aria-hidden="true" />
                Tap the map to place a pin
              </div>
            )
          )}
        </div>

        <div className="lp-chin">
          {geoError && (
            <p className="lp-chin__error">
              <i className="fas fa-circle-exclamation" aria-hidden="true" />
              {geoError}
            </p>
          )}

          <div className="lp-chin__row">
            <span
              className={`lp-chin__address${pin ? "" : " lp-chin__address--empty"}`}
            >
              <i
                className={`fas ${
                  geocoding ? "fa-spinner fa-spin" : "fa-map-marker-alt"
                } lp-chin__pin`}
                aria-hidden="true"
              />
              <span className="lp-chin__text">
                {geocoding
                  ? "Getting address…"
                  : pin
                    ? address || `${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)}`
                    : "No location set yet"}
              </span>
            </span>

            <div className="lp-chin__actions">
              <button type="button" className="lp-btn" onClick={onCancel}>
                Cancel
              </button>
              {/* Was "Proceed", which does not say what it commits you to. */}
              <button
                type="button"
                className="lp-btn lp-btn--primary"
                onClick={handleProceed}
                disabled={!pin || geocoding}
              >
                Use this location
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LocationPicker;
